import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Bot, User, Sparkles, AlertCircle, Lightbulb, Wand2, PenTool, TrendingUp, Hash, Calendar, MessageSquare } from 'lucide-react';
import { streamOpenAIResponse } from '../lib/openai';
import ReactMarkdown from 'react-markdown';

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
  pending?: boolean;
}

interface QuickAction {
  id: string;
  label: string;
  prompt: string;
  icon: typeof Wand2;
  color: string;
}

const quickActions: QuickAction[] = [
  {
    id: 'post-ideas',
    label: 'Post Ideas',
    prompt: 'Generate 5 engaging post ideas for my social media',
    icon: PenTool,
    color: 'text-blue-500 dark:text-blue-400'
  },
  {
    id: 'captions',
    label: 'Generate Captions',
    prompt: 'Write 3 engaging captions for a social media post',
    icon: MessageSquare,
    color: 'text-purple-500 dark:text-purple-400'
  },
  {
    id: 'trending',
    label: 'Trending Topics',
    prompt: 'What are some trending topics I should create content about?',
    icon: TrendingUp,
    color: 'text-green-500 dark:text-green-400'
  },
  {
    id: 'hashtags',
    label: 'Hashtag Sets',
    prompt: 'Suggest relevant hashtag sets for my content',
    icon: Hash,
    color: 'text-rose-500 dark:text-rose-400'
  },
  {
    id: 'schedule',
    label: 'Post Schedule',
    prompt: 'Help me create an optimal posting schedule',
    icon: Calendar,
    color: 'text-amber-500 dark:text-amber-400'
  }
];

const tips = [
  "Ask for specific platform advice (e.g., 'How can I increase my Instagram engagement?')",
  "Request content ideas for specific occasions or events",
  "Get help with content repurposing strategies",
  "Ask for headline and hook variations",
  "Get feedback on your content strategy"
];

const CreatorAssistant = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pendingMessageRef = useRef<string>('');
  const initialLoadRef = useRef<boolean>(true);
  const [currentTip, setCurrentTip] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();

  // Rotate tips
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTip(prev => (prev + 1) % tips.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check for welcome message on initial load
  useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      const welcomeMessage = localStorage.getItem('welcome_message');
      
      if (welcomeMessage) {
        // Clear the welcome message from localStorage
        localStorage.removeItem('welcome_message');
        
        // Create and add the user message
        const userMessage: Message = {
          id: crypto.randomUUID(),
          content: welcomeMessage.trim(),
          sender: 'user',
          timestamp: new Date(),
        };
        
        // Create the assistant message
        const assistantMessage: Message = {
          id: crypto.randomUUID(),
          content: '',
          sender: 'assistant',
          timestamp: new Date(),
          pending: true,
        };
        
        // Add both messages to the chat
        setMessages([userMessage, assistantMessage]);
        
        // Process the assistant response
        processAssistantResponse(welcomeMessage.trim(), assistantMessage.id);
      }
    }
  }, []);

  const processAssistantResponse = async (userInput: string, assistantMessageId: string) => {
    setIsLoading(true);
    setError(null);
    pendingMessageRef.current = '';

    try {
      await streamOpenAIResponse(
        messages
          .filter(m => !m.pending)
          .map(m => ({
            role: m.sender as 'user' | 'assistant',
            content: m.content,
          }))
          .concat([{ role: 'user' as const, content: userInput }]),
        (chunk) => {
          pendingMessageRef.current += chunk;
          setMessages(prev =>
            prev.map(m =>
              m.id === assistantMessageId
                ? { ...m, content: pendingMessageRef.current }
                : m
            )
          );
        },
        (error) => {
          setError(error.message);
          // Remove the pending message if there's an error
          setMessages(prev => prev.filter(m => m.id !== assistantMessageId));
        }
      );
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('An unknown error occurred');
      }
      // Ensure we remove the pending message if there's an error
      setMessages(prev => prev.filter(m => m.id !== assistantMessageId));
    } finally {
      setIsLoading(false);
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantMessageId ? { ...m, pending: false } : m
        )
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      content: input.trim(),
      sender: 'user',
      timestamp: new Date(),
    };

    const assistantMessage: Message = {
      id: crypto.randomUUID(),
      content: '',
      sender: 'assistant',
      timestamp: new Date(),
      pending: true,
    };

    setMessages(prev => [...prev, userMessage, assistantMessage]);
    const userInput = input.trim();
    setInput('');
    
    await processAssistantResponse(userInput, assistantMessage.id);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="h-[calc(100vh-8rem)] flex flex-col space-y-6"
    >
      {/* Rotating Tip Banner */}
      <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-4 border border-indigo-100 dark:border-indigo-800/30">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
            <Lightbulb className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentTip}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex-1"
            >
              <p className="text-sm text-indigo-900 dark:text-indigo-100">
                {tips[currentTip]}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
        {quickActions.map((action) => {
          const Icon = action.icon;
          return (
            <motion.button
              key={action.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setInput(action.prompt);
                handleSubmit(new Event('submit') as React.FormEvent);
              }}
              className="p-4 bg-white dark:bg-dark-800 rounded-xl border border-gray-100 dark:border-dark-700 hover:shadow-md transition-all text-center group"
            >
              <Icon className={`h-5 w-5 mx-auto mb-2 ${action.color}`} />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                {action.label}
              </span>
            </motion.button>
          );
        })}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 bg-white dark:bg-dark-800 rounded-xl shadow-sm p-6">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: "reverse"
              }}
              className="relative"
            >
              <div className="absolute inset-0 bg-indigo-400/20 dark:bg-indigo-600/20 rounded-full blur-xl" />
              <div className="relative p-6 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full">
                <Bot className="h-12 w-12 text-white" />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                How can I help you create today?
              </h2>
              <p className="text-gray-600 dark:text-gray-400 max-w-md">
                I'm your AI assistant, ready to help with content ideas, strategy, and optimization.
                Try one of the quick actions above or ask me anything!
              </p>
            </motion.div>
          </div>
        ) : (
          <div className="space-y-6">
            {messages.map(message => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex items-start space-x-2 ${
                  message.sender === 'assistant' ? 'flex-row' : 'flex-row-reverse space-x-reverse'
                }`}
              >
                <div className={`p-2 rounded-full ${
                  message.sender === 'assistant'
                    ? 'bg-indigo-50 dark:bg-indigo-900/20'
                    : 'bg-gray-100 dark:bg-dark-700'
                }`}>
                  {message.sender === 'assistant' ? (
                    <Bot className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  ) : (
                    <User className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  )}
                </div>
                <div className={`flex-1 ${
                  message.sender === 'assistant' ? 'mr-12' : 'ml-12'
                }`}>
                  <div className={`p-4 rounded-xl ${
                    message.sender === 'assistant'
                      ? 'bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 shadow-sm'
                      : 'bg-indigo-600 text-white'
                  }`}>
                    <div className={
                      message.sender === 'assistant'
                        ? 'prose dark:prose-invert max-w-none'
                        : 'text-white'
                    }>
                      {message.sender === 'assistant' ? (
                        <ReactMarkdown>{message.content || ' '}</ReactMarkdown>
                      ) : (
                        message.content
                      )}
                      {message.pending && (
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: [0, 1, 0] }}
                          transition={{ repeat: Infinity, duration: 1.5 }}
                          className="inline-block ml-1"
                        >
                          ▋
                        </motion.span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {message.timestamp.toLocaleTimeString()}
                  </p>
                </div>
              </motion.div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/5 to-purple-500/5 rounded-xl"></div>
        <form onSubmit={handleSubmit} className="relative p-1.5">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask me anything about content creation..."
            className="w-full px-4 py-3 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 shadow-sm"
            disabled={isLoading}
          />
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="submit"
            disabled={isLoading}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-full hover:bg-gray-100 dark:hover:bg-dark-700 transition-all"
          >
            <Send className="h-5 w-5" />
          </motion.button>
        </form>
      </div>
    </motion.div>
  );
};

export default CreatorAssistant;